import { useCallback, useEffect, useMemo, useState } from "react";
import { message } from "antd";

import { getKnowledgebases } from "V2.0/HomePages/Knowledge_base/service";
import { showErrorToast } from "V2.0/utils";

import { Switch } from "V2.0/common/switch";
import { Button } from "V2.0/common/button";
import { Checkbox, CheckboxField, CheckboxGroup } from "V2.0/common/checkbox";

import { AiOutlineLoading3Quarters } from "react-icons/ai";

export default AssistantCoachKnowledge;

function AssistantCoachKnowledge(props) {
  const { assistantCoach, workspaceId, onUpdateAssistant, isUnlocked } = props;

  const [kbSettings, setKbSettings] = useState({
    enableKBSearch: assistantCoach?.kbSettings?.enableKBSearch ?? false,
    knowledgeBaseIds: assistantCoach?.kbSettings?.knowledgeBaseIds ?? [],
  });

  const [availableKnowledgeBases, setAvailableKnowledgeBases] = useState({
    data: [],
    isLoading: true,
  });

  const [loading, setLoading] = useState<boolean>(false);

  const fetchKnowledgeBases = useCallback(async (workspaceId) => {
    try {
      const response = await getKnowledgebases({ projectId: workspaceId });
      if (response.status === 200) {
        const data = response.data;

        setAvailableKnowledgeBases({
          data: data.knowledgebases,
          isLoading: false,
        });
      }
    } catch (e) {
      setAvailableKnowledgeBases((prevState) => {
        return {
          ...prevState,
          isLoading: false,
        };
      });
    }
  }, []);

  useEffect(() => {
    if (workspaceId) {
      fetchKnowledgeBases(workspaceId);
    }
  }, [workspaceId]);

  useEffect(() => {
    if (availableKnowledgeBases.data.length > 0) {
      const availableKnowledgeBaseIds = availableKnowledgeBases.data.map(
        (kb) => kb._id,
      );
      setKbSettings((prev) => {
        return {
          ...prev,
          knowledgeBaseIds: prev.knowledgeBaseIds.filter((kbId) =>
            availableKnowledgeBaseIds.includes(kbId),
          ),
        };
      });
    }
  }, [availableKnowledgeBases.data]);

  const handleSave = useCallback(async (data) => {
    const payload = {
      kbSettings: data,
    };

    const lifecycleCbs = {
      init() {
        setLoading(true);
        message.loading("Updating Knowledge Base");
      },
      success() {
        setLoading(false);
        message.destroy();
        message.success("Knowledge base updated");
      },
      error(e) {
        setLoading(false);
        message.destroy();
        showErrorToast(e, "Failed to update knowledge base");
      },
    };

    onUpdateAssistant(payload, lifecycleCbs);
  }, []);

  const knowledgeBaseList = useMemo(() => {
    if (availableKnowledgeBases.data.length > 0) {
      return availableKnowledgeBases.data.map((kb) => {
        return {
          id: kb._id,
          label: kb.label,
          isSelected: kbSettings?.knowledgeBaseIds?.includes(kb._id),
          disabled: !isUnlocked || !kbSettings.enableKBSearch,
        };
      });
    }

    return [];
  }, [kbSettings.knowledgeBaseIds, kbSettings.enableKBSearch, isUnlocked]);

  const isKnowledgeBasesLoading = availableKnowledgeBases.isLoading;
  const isAvailableKnowledgeBasesEmpty =
    availableKnowledgeBases.data?.length === 0;
  const isKnowledgeBaseListAvailable = knowledgeBaseList.length > 0;

  const renderKnowledgeBasesLoading = (
    <div className="min-h-48 flex items-center justify-center">
      <AiOutlineLoading3Quarters className="animate-spin" />
    </div>
  );

  const renderKnowledgeBaseEmpty = (
    <p className="text-sm">No Knowledge bases created</p>
  );

  const renderKnowledgeBaseList = (
    <CheckboxGroup className="space-y-4">
      {knowledgeBaseList.map((kb) => {
        return (
          <KnowledgeBaseCheckbox
            key={kb.id}
            id={kb.id}
            label={kb.label}
            disabled={kb.disabled}
            isSelected={kb.isSelected}
            setSelectedKnowledgeBases={setKbSettings}
          />
        );
      })}
    </CheckboxGroup>
  );

  const isLoading = isKnowledgeBasesLoading;
  const isEmpty = !isKnowledgeBasesLoading && isAvailableKnowledgeBasesEmpty;
  const isAvailable = !isKnowledgeBasesLoading && isKnowledgeBaseListAvailable;

  return (
    <div className="w-2/4 p-4 h-full">
      <div className="flex flex-col border rounded-lg">
        <div className="px-6 py-4 bg-gray-50 rounded-t-lg flex items-center gap-x-2">
          <p className="text-md font-semibold text-gray-900">Knowledge Base</p>
        </div>
        <div className="flex flex-col space-y-4 p-8">
          <div className="flex items-center gap-x-2">
            <p className="text-md font-medium text-gray-900">
              Use Knowledge Base
            </p>
            <Switch
              color="emerald"
              disabled={!isUnlocked}
              checked={kbSettings.enableKBSearch}
              onChange={() => {
                setKbSettings((prev) => {
                  return {
                    ...prev,
                    enableKBSearch: !prev.enableKBSearch,
                  };
                });
              }}
            />
          </div>
          <div className="border rounded-lg p-4">
            {isLoading && renderKnowledgeBasesLoading}
            {isEmpty && renderKnowledgeBaseEmpty}
            {isAvailable && renderKnowledgeBaseList}
          </div>
        </div>
        {isUnlocked && (
          <div className="bg-gray-50 text-right rounded-b-lg px-6 py-3">
            <Button
              loading={loading}
              disabled={loading || !kbSettings?.enableKBSearch}
              onClick={() => handleSave(kbSettings)}
            >
              Save
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function KnowledgeBaseCheckbox(props) {
  const { id, label, isSelected, disabled, setSelectedKnowledgeBases } = props;

  const handleChange = () => {
    setSelectedKnowledgeBases((prev) => {
      const knowledgeBaseIds = isSelected
        ? prev.knowledgeBaseIds?.filter((kbId) => kbId != id)
        : [...prev.knowledgeBaseIds, id];

      return {
        ...prev,
        knowledgeBaseIds,
      };
    });
  };

  return (
    <CheckboxField>
      <Checkbox
        name={id}
        checked={isSelected}
        disabled={disabled}
        onChange={handleChange}
      />
      <p className="text-sm">{label}</p>
    </CheckboxField>
  );
}
